export interface UserInfo {
  id: number;
  publicId: string;
  nickname: string;
  phone?: string;
  passwordSet: boolean;
  status: "active" | "disabled" | "closing" | "closed";
  tier: "normal" | "tester";
  featureOverrides: Record<string, boolean>;
  registeredAt?: string;
  createdAt?: string;
  closureRequestedAt?: string;
  closedAt?: string;
}

export interface UserMeResponse {
  ok: true;
  user: UserInfo;
  credits?: { balance?: number };
  features?: {
    revision: string;
    edition: "global" | "china";
    editionRevision: string;
    capabilities: Record<string, boolean>;
    enabled: Record<string, boolean>;
  };
  uploadLimits?: {
    maxTotalMb: number;
    audience: "member" | "non_member";
    membershipPlan?: string;
  };
}

export interface MembershipProduct {
  key: "membership_month" | "membership_year";
  name: string;
  description: string;
  durationMonths: number;
  priceFen: number;
  currency: "CNY";
  available: boolean;
}

export interface UserMembership {
  publicId: string;
  status: "active" | "expired";
  planKey: "membership_month" | "membership_year";
  startsAt: string;
  expiresAt: string;
}

export interface PaymentOrder {
  id: string;
  outTradeNo: string;
  productKey: MembershipProduct["key"];
  productName: string;
  amountTotal: number;
  currency: string;
  status: "creating" | "notpay" | "success" | "closed" | "expired" | "failed";
  expiresAt: string;
  paidAt?: string;
  activatedAt?: string;
  failureMessage?: string;
}

export interface AdminSessionResponse {
  ok: true;
  session: { username: string; role: "super_admin" | "support" | "viewer" | "tester"; expiresAt: string };
  permissions: string[];
}

export interface AppPublicConfigResponse {
  ok: true;
  edition: {
    edition: "global" | "china";
    revision: string;
    capabilities: Record<string, boolean>;
  };
  deploymentId: string;
  auth: {
    captcha: { enabled: boolean; provider: "altcha" | "image" | "disabled"; expiresInSeconds: number; minDurationMs: number };
    phoneCode: { enabled: boolean; expiresInSeconds: number; resendAfterSeconds: number };
    devLoginEnabled: boolean;
  };
}

export interface CaptchaChallenge {
  id: string;
  type: "text" | "click" | "slider";
  imageDataUrl: string;
  prompt: string;
  expiresInSeconds: number;
  width: number;
  height: number;
  clickCount?: number;
  sliderStart?: number;
}

export class ApiError extends Error {
  constructor(readonly status: number, message: string, readonly code?: string, readonly requestId?: string) {
    super(message);
  }
}

export async function userApi<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getUserAuthToken();
  return request<T>(path, options, token ? { authorization: `Bearer ${token}` } : {});
}

export function getUserAuthToken(): string {
  return localStorage.getItem("pptdeep_user_token") || localStorage.getItem("df_ai_ppt_token") || "";
}

export async function adminApi<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getUserAuthToken();
  return request<T>(
    path,
    { ...options, credentials: "same-origin" },
    token ? { authorization: `Bearer ${token}` } : {},
  );
}

async function request<T>(path: string, options: RequestInit, extraHeaders: Record<string, string> = {}): Promise<T> {
  const response = await fetch(path, {
    ...options,
    headers: {
      ...(options.body == null || options.body instanceof FormData ? {} : { "content-type": "application/json" }),
      ...extraHeaders,
      ...(options.headers || {}),
    },
  });
  const data = await response.json().catch(() => ({})) as Record<string, unknown>;
  if (!response.ok || data.ok === false) {
    throw new ApiError(response.status, String(data.error || "请求失败"), typeof data.code === "string" ? data.code : undefined, response.headers.get("x-request-id") || undefined);
  }
  return data as T;
}
